import { ref } from 'vue'
import type { Ref } from 'vue'
import { listVenues, showVenue, createVenue, updateVenue, deleteVenue } from '@/api/venues'
import type { Venue, StoreVenuePayload, UpdateVenuePayload } from '@/types/events'

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data: unknown } }).response !== null
  )
}

/**
 * The 422 body returned by DELETE venues when the venue is still referenced by
 * an active edition. It is NOT a Laravel validation envelope: branch on the
 * stable `error` discriminator, never on `message` (English-only, developer
 * facing, and never shown to a user).
 */
function isVenueInUseError(
  e: unknown,
): e is { response: { status: number; data: { error: string; active_edition_count: number } } } {
  if (
    typeof e !== 'object' ||
    e === null ||
    !('response' in e) ||
    typeof (e as { response: unknown }).response !== 'object' ||
    (e as { response: unknown }).response === null
  ) {
    return false
  }

  const response = (e as { response: { status?: unknown; data?: unknown } }).response

  if (response.status !== 422) return false
  if (typeof response.data !== 'object' || response.data === null) return false

  return (response.data as { error?: unknown }).error === 'venue_in_use'
}

export type DeleteVenueResult = { ok: true } | { ok: false; activeEditionCount: number }

/**
 * In-flight `GET .../venues` requests, keyed by organisation.
 *
 * RaceForm mounts two VenueSelect instances, so two byte-identical list
 * requests fired on every race page. Callers that ask while a request for the
 * same organisation is still in flight now share that single request.
 *
 * This is deliberately a request dedupe and NOT a cache. The entry is dropped
 * as soon as the request settles, so every later fetch (the refresh after a 422,
 * a remount, a navigation) still goes to the network and can never serve stale
 * data. Each caller also receives its own array copy, so the per-instance
 * `venues` state that create and delete mutate is completely unchanged.
 */
const inFlightVenueLists = new Map<number, Promise<Venue[]>>()

function listVenuesDeduped(organisationId: number): Promise<Venue[]> {
  const pending = inFlightVenueLists.get(organisationId)
  if (pending !== undefined) {
    return pending
  }

  const request = listVenues(organisationId).finally(() => {
    inFlightVenueLists.delete(organisationId)
  })

  inFlightVenueLists.set(organisationId, request)

  return request
}

export function useVenues(organisationId: number): {
  venues: Ref<Venue[]>
  loading: Ref<boolean>
  error: Ref<string | null>
  createErrors: Ref<Record<string, string[]>>
  deleting: Ref<boolean>
  deleteError: Ref<string | null>
  updateErrors: Ref<Record<string, string[]>>
  updating: Ref<boolean>
  fetchVenues: () => Promise<void>
  fetchVenue: (venueId: number) => Promise<Venue | null>
  createVenue: (payload: StoreVenuePayload) => Promise<Venue | null>
  updateVenue: (venueId: number, payload: UpdateVenuePayload) => Promise<Venue | null>
  deleteVenue: (venueId: number) => Promise<DeleteVenueResult>
} {
  const venues = ref<Venue[]>([])
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  const createErrors = ref<Record<string, string[]>>({})
  const deleting = ref<boolean>(false)
  const deleteError = ref<string | null>(null)
  const updateErrors = ref<Record<string, string[]>>({})
  const updating = ref<boolean>(false)

  async function fetchVenues(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      // Copied, not aliased: two pickers sharing one in-flight request must
      // still own independent arrays, or a create/delete in one would mutate
      // the other's list.
      venues.value = [...(await listVenuesDeduped(organisationId))]
    } catch {
      error.value = 'venues.load_error'
    } finally {
      loading.value = false
    }
  }

  /** Fresh single venue (including its usage counts); null when it could not be loaded. */
  async function fetchVenue(venueId: number): Promise<Venue | null> {
    try {
      return await showVenue(organisationId, venueId)
    } catch {
      return null
    }
  }

  /** Update a venue and replace its entry in the list; null on failure (422 fills updateErrors). */
  async function update(venueId: number, payload: UpdateVenuePayload): Promise<Venue | null> {
    updateErrors.value = {}
    updating.value = true
    try {
      const venue = await updateVenue(organisationId, venueId, payload)
      venues.value = venues.value.map((existing) => (existing.id === venue.id ? venue : existing))
      return venue
    } catch (e: unknown) {
      if (isValidationError(e)) {
        updateErrors.value = e.response.data.errors
      }
      return null
    } finally {
      updating.value = false
    }
  }

  async function create(payload: StoreVenuePayload): Promise<Venue | null> {
    createErrors.value = {}
    loading.value = true
    try {
      const venue = await createVenue(organisationId, payload)
      venues.value = [...venues.value, venue]
      return venue
    } catch (e: unknown) {
      if (isValidationError(e)) {
        createErrors.value = e.response.data.errors
      }
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete a venue. Never throws for the documented 422 `venue_in_use` path:
   * it returns the authoritative active-edition count so the caller can render
   * its own translated blocked hint.
   */
  async function remove(venueId: number): Promise<DeleteVenueResult> {
    deleting.value = true
    deleteError.value = null
    try {
      await deleteVenue(organisationId, venueId)
      venues.value = venues.value.filter((venue) => venue.id !== venueId)
      return { ok: true }
    } catch (e: unknown) {
      if (isVenueInUseError(e)) {
        return { ok: false, activeEditionCount: e.response.data.active_edition_count }
      }
      deleteError.value = 'venues.delete.error'
      return { ok: false, activeEditionCount: 0 }
    } finally {
      deleting.value = false
    }
  }

  return {
    venues,
    loading,
    error,
    createErrors,
    deleting,
    deleteError,
    updateErrors,
    updating,
    fetchVenues,
    fetchVenue,
    createVenue: create,
    updateVenue: update,
    deleteVenue: remove,
  }
}
